import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, CardBody, CardHeader, Table, Badge,
  Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, Spinner, Alert
} from 'reactstrap';
import { Database, Plus, Edit, Trash2, Play, Eye } from 'react-feather';
import API from '../utils/api';

const DataSources = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Stats and progress
  const [stats, setStats] = useState(null);
  const [progress, setProgress] = useState([]);

  // Modals
  const [editModal, setEditModal] = useState(false);
  const [testModal, setTestModal] = useState(false);
  const [mapperModal, setMapperModal] = useState(false);
  const [browseModal, setBrowseModal] = useState(false);
  const [currentSource, setCurrentSource] = useState(null);

  // Browse data
  const [browseData, setBrowseData] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browsePage, setBrowsePage] = useState(1);
  const [browseTotal, setBrowseTotal] = useState(0);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    api_url: '',
    auth_type: 'none',
    auth_credentials: {},
    request_method: 'POST',
    request_body_template: '',
    request_headers: {},
    response_format: 'json',
    response_mapping: null,
    batch_size: 100,
    parallel_workers: 1,
    indexed_params_enabled: false,
    indexed_params_prefix: 'item',
    indexed_params_count_key: 'items',
    indexed_params_start_index: 0,
    is_active: true
  });

  // Test data
  const [testDomains, setTestDomains] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // Mapper data
  const [exampleResponse, setExampleResponse] = useState('');
  const [analyzedStructure, setAnalyzedStructure] = useState(null);
  const [mapping, setMapping] = useState({
    array_path: '',
    domain_field: '',
    main_value_field: '',
    main_value_label: '',
    extra_fields: {},
    additional_values: []
  });

  useEffect(() => {
    loadSources();
    loadStats();
    loadProgress();

    // Auto-refresh stats and progress every 5 seconds
    const interval = setInterval(() => {
      loadStats();
      loadProgress();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadSources = async () => {
    try {
      setLoading(true);
      const response = await API.get('/data-sources/');
      setSources(response.data.sources || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data sources');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await API.get('/data-sources/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadProgress = async () => {
    try {
      const response = await API.get('/data-sources/progress');
      setProgress(response.data.active_jobs || []);
    } catch (err) {
      console.error('Failed to load progress:', err);
    }
  };

  const openCreateModal = () => {
    setCurrentSource(null);
    setFormData({
      name: '',
      description: '',
      api_url: '',
      auth_type: 'none',
      auth_credentials: {},
      request_method: 'POST',
      request_body_template: '',
      request_headers: {},
      response_format: 'json',
      response_mapping: null,
      batch_size: 100,
      parallel_workers: 1,
      is_active: true
    });
    setEditModal(true);
  };

  const openEditModal = (source) => {
    setCurrentSource(source);
    setFormData(source);
    setEditModal(true);
  };

  const handleSave = async () => {
    try {
      if (currentSource) {
        await API.put(`/data-sources/${currentSource.id}`, formData);
        setSuccess('Data source updated successfully');
      } else {
        await API.post('/data-sources/', formData);
        setSuccess('Data source created successfully');
      }

      setEditModal(false);
      loadSources();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save data source');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this data source?')) {
      return;
    }

    try {
      await API.delete(`/data-sources/${id}`);
      setSuccess('Data source deleted successfully');
      loadSources();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete data source');
    }
  };

  const handleCreateTable = async (source) => {
    if (!window.confirm(`Create/Recreate ClickHouse table for "${source.name}"?`)) {
      return;
    }

    try {
      const response = await API.post(`/data-sources/${source.id}/create-table`);
      if (response.data.success) {
        setSuccess(`Table ${response.data.table_name} created successfully`);
      } else {
        setError('Table creation failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create table');
    }
  };

  const openTestModal = (source) => {
    setCurrentSource(source);
    setTestDomains('');
    setTestResults(null);
    setTestModal(true);
  };

  const handleTest = async () => {
    try {
      setTestLoading(true);
      const domains = testDomains.split('\n').map(d => d.trim()).filter(d => d);

      const response = await API.post(`/data-sources/${currentSource.id}/test`, {
        domains
      });

      setTestResults(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Test failed');
    } finally {
      setTestLoading(false);
    }
  };

  const openMapperModal = () => {
    setExampleResponse('');
    setAnalyzedStructure(null);
    setMapperModal(true);
  };

  const analyzeResponse = async () => {
    try {
      const response = await API.post('/data-sources/analyze', {
        response_text: exampleResponse,
        format_type: formData.response_format
      });

      setAnalyzedStructure(response.data);

      // Reset mapping when re-analyzing
      setMapping({
        array_path: '',
        domain_field: '',
        main_value_field: '',
        main_value_label: '',
        extra_fields: {},
        additional_values: []
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to analyze response');
    }
  };

  const updateArrayPath = (arrayPath) => {
    setMapping({...mapping, array_path: arrayPath});

    // When array path changes, extract fields from items in that array
    if (arrayPath && analyzedStructure) {
      try {
        const parsed = JSON.parse(exampleResponse);
        let arrayData = parsed;

        // Navigate to array
        const parts = arrayPath.split('.');
        for (let part of parts) {
          if (part.includes('[')) {
            const [key, index] = part.split('[');
            const idx = parseInt(index.replace(']', ''));
            arrayData = key ? arrayData[key][idx] : arrayData[idx];
          } else {
            arrayData = arrayData[part];
          }
        }

        // Get fields from first item in array
        if (Array.isArray(arrayData) && arrayData.length > 0) {
          const firstItem = arrayData[0];
          const itemFields = Object.keys(firstItem);

          // Update available fields to only show item-level fields
          setAnalyzedStructure({
            ...analyzedStructure,
            itemFields: itemFields
          });
        }
      } catch (e) {
        console.error('Failed to extract item fields:', e);
      }
    }
  };

  const applyMapping = () => {
    // Add format to mapping
    const fullMapping = {
      ...mapping,
      format: formData.response_format
    };
    setFormData({...formData, response_mapping: fullMapping});
    setMapperModal(false);
    setSuccess('Response mapping applied');
  };

  const openBrowseModal = async (source) => {
    setCurrentSource(source);
    setBrowseModal(true);
    setBrowsePage(1);
    await loadBrowseData(source.id, 1);
  };

  const loadBrowseData = async (sourceId, page = 1) => {
    try {
      setBrowseLoading(true);
      const response = await API.get(`/data-sources/${sourceId}/browse?page=${page}&per_page=50`);
      setBrowseData(response.data.data || []);
      setBrowseTotal(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setBrowseLoading(false);
    }
  };

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="page-heading">
                <Database className="page-heading-icon" />
                Data Sources
              </h2>
              <small className="text-muted">
                External API sources for domain enrichment
              </small>
            </div>
            <Button color="primary" onClick={openCreateModal}>
              <Plus size={16} /> New Data Source
            </Button>
          </div>
        </Col>
      </Row>

      {error && <Alert color="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert color="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Stats Cards */}
      {stats && (
        <Row className="mb-4">
          <Col md={4}>
            <Card className="bg-dark border-secondary">
              <CardBody>
                <h6 className="text-muted mb-2">Total Domains</h6>
                <h3 className="mb-0">{stats.total_domains?.toLocaleString()}</h3>
              </CardBody>
            </Card>
          </Col>
          {stats.per_source?.map(source => (
            <Col md={4} key={source.id}>
              <Card className="bg-dark border-secondary">
                <CardBody>
                  <h6 className="text-muted mb-2">{source.name}</h6>
                  <h4 className="mb-2 text-info">{source.enriched?.toLocaleString()} enriched</h4>
                  <div className="progress" style={{height: '8px'}}>
                    <div
                      className="progress-bar bg-info"
                      style={{width: `${source.percentage}%`}}
                    />
                  </div>
                  <small className="text-muted">{source.percentage}% coverage</small>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Active Jobs Progress */}
      {progress.length > 0 && (
        <Card className="bg-dark border-warning mb-4">
          <CardHeader className="bg-warning text-dark">
            <strong>Active Enrichment Jobs</strong>
          </CardHeader>
          <CardBody>
            {progress.map((job, idx) => (
              <div key={idx} className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <strong>{job.source_name}</strong>
                  <span>{job.processed?.toLocaleString()} / {job.total_domains?.toLocaleString()} domains</span>
                </div>
                <div className="progress mb-1" style={{height: '20px'}}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated bg-info"
                    style={{width: `${job.progress}%`}}
                  >
                    {job.progress}%
                  </div>
                </div>
                <small className="text-muted">Status: {job.status} | Started: {new Date(job.started_at).toLocaleTimeString()}</small>
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      {/* Data Sources Table */}
      <Card className="bg-dark border-secondary">
        <CardHeader>
          Data Sources ({sources.length})
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : sources.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No data sources configured. Create one to get started.
            </div>
          ) : (
            <Table dark hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>API URL</th>
                  <th>Method</th>
                  <th>Format</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sources.map(source => (
                  <tr key={source.id}>
                    <td><strong>{source.name}</strong></td>
                    <td><small className="text-muted">{source.api_url}</small></td>
                    <td>
                      <Badge className="bg-dark text-info border border-info">
                        {source.request_method}
                      </Badge>
                    </td>
                    <td>
                      <Badge className="bg-dark text-warning border border-warning">
                        {source.response_format}
                      </Badge>
                    </td>
                    <td>
                      {source.is_active ? (
                        <Badge className="bg-dark text-success border border-success">Active</Badge>
                      ) : (
                        <Badge className="bg-dark text-secondary border border-secondary">Inactive</Badge>
                      )}
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <Button
                          size="sm"
                          color="outline-info"
                          onClick={() => openBrowseModal(source)}
                          title="Browse Data"
                        >
                          <Eye size={14} />
                        </Button>
                        <Button
                          size="sm"
                          color="outline-success"
                          onClick={() => openTestModal(source)}
                          title="Test"
                        >
                          <Play size={14} />
                        </Button>
                        <Button
                          size="sm"
                          color="outline-primary"
                          onClick={() => handleCreateTable(source)}
                          title="Create/Recreate ClickHouse Table"
                        >
                          <Database size={14} />
                        </Button>
                        <Button
                          size="sm"
                          color="outline-warning"
                          onClick={() => openEditModal(source)}
                          title="Edit"
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          size="sm"
                          color="outline-danger"
                          onClick={() => handleDelete(source.id)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Edit/Create Modal */}
      <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="lg">
        <ModalHeader toggle={() => setEditModal(false)}>
          {currentSource ? 'Edit Data Source' : 'New Data Source'}
        </ModalHeader>
        <ModalBody>
          <Form>
            {/* Basic Info */}
            <FormGroup>
              <Label>Name *</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="My Data Source"
              />
            </FormGroup>

            <FormGroup>
              <Label>Description</Label>
              <Input
                type="textarea"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="What data does this source provide?"
                rows={2}
              />
            </FormGroup>

            <FormGroup>
              <Label>API URL *</Label>
              <Input
                type="text"
                value={formData.api_url}
                onChange={(e) => setFormData({...formData, api_url: e.target.value})}
                placeholder="https://api.example.com/lookup?domain={DOMAIN}"
              />
              <small className="text-muted">
                Use {'{DOMAIN}'} for single domain requests (iterates one by one)
              </small>
            </FormGroup>

            {/* Authentication */}
            <h6 className="mt-4">Authentication</h6>
            <FormGroup>
              <Label>Auth Type</Label>
              <Input
                type="select"
                value={formData.auth_type}
                onChange={(e) => setFormData({...formData, auth_type: e.target.value})}
              >
                <option value="none">None</option>
                <option value="basic">Basic Auth</option>
                <option value="bearer">Bearer Token</option>
                <option value="api_key">API Key</option>
              </Input>
            </FormGroup>

            {formData.auth_type === 'basic' && (
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Username</Label>
                    <Input
                      type="text"
                      value={formData.auth_credentials?.username || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        auth_credentials: {...formData.auth_credentials, username: e.target.value}
                      })}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={formData.auth_credentials?.password || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        auth_credentials: {...formData.auth_credentials, password: e.target.value}
                      })}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}

            {formData.auth_type === 'bearer' && (
              <FormGroup>
                <Label>Bearer Token</Label>
                <Input
                  type="password"
                  value={formData.auth_credentials?.token || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    auth_credentials: {token: e.target.value}
                  })}
                />
              </FormGroup>
            )}

            {formData.auth_type === 'api_key' && (
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Header Name</Label>
                    <Input
                      type="text"
                      value={formData.auth_credentials?.key || 'X-API-Key'}
                      onChange={(e) => setFormData({
                        ...formData,
                        auth_credentials: {...formData.auth_credentials, key: e.target.value}
                      })}
                      placeholder="X-API-Key"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>API Key</Label>
                    <Input
                      type="password"
                      value={formData.auth_credentials?.value || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        auth_credentials: {...formData.auth_credentials, value: e.target.value}
                      })}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}

            {/* Request Configuration */}
            <h6 className="mt-4">Request Configuration</h6>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Request Method</Label>
                  <Input
                    type="select"
                    value={formData.request_method}
                    onChange={(e) => setFormData({...formData, request_method: e.target.value})}
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Response Format</Label>
                  <Input
                    type="select"
                    value={formData.response_format}
                    onChange={(e) => setFormData({...formData, response_format: e.target.value})}
                  >
                    <option value="json">JSON</option>
                    <option value="xml">XML</option>
                    <option value="csv">CSV</option>
                    <option value="text">Text</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Request Body Template</Label>
              <Input
                type="textarea"
                value={formData.request_body_template}
                onChange={(e) => setFormData({...formData, request_body_template: e.target.value})}
                placeholder='{"targets": {DOMAINS_LIST}, "api_key": "..."}'
                rows={4}
              />
              <small className="text-muted">
                Use {'{DOMAIN}'} in body for single domain or {'{DOMAINS_LIST}'} for batch array
              </small>
            </FormGroup>

            {/* Indexed Parameters */}
            <h6 className="mt-4">Indexed Parameters</h6>
            <FormGroup check className="mb-3">
              <Label check>
                <Input
                  type="checkbox"
                  checked={formData.indexed_params_enabled}
                  onChange={(e) => setFormData({...formData, indexed_params_enabled: e.target.checked})}
                />
                {' '}Use Indexed Parameters (e.g., item0=domain1&item1=domain2)
              </Label>
              <div className="ms-4 mt-2">
                <small className="text-muted">
                  Use {'{DOMAINS_INDEXED}'} in URL to automatically generate indexed parameters
                </small>
              </div>
            </FormGroup>

            {formData.indexed_params_enabled && (
              <Row className="mb-3">
                <Col md={4}>
                  <FormGroup>
                    <Label>Parameter Prefix</Label>
                    <Input
                      type="text"
                      value={formData.indexed_params_prefix}
                      onChange={(e) => setFormData({...formData, indexed_params_prefix: e.target.value})}
                      placeholder="item"
                    />
                    <small className="text-muted">Result: item0, item1, ...</small>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Count Parameter</Label>
                    <Input
                      type="text"
                      value={formData.indexed_params_count_key}
                      onChange={(e) => setFormData({...formData, indexed_params_count_key: e.target.value})}
                      placeholder="items"
                    />
                    <small className="text-muted">Result: items=3</small>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Start Index</Label>
                    <Input
                      type="select"
                      value={formData.indexed_params_start_index}
                      onChange={(e) => setFormData({...formData, indexed_params_start_index: parseInt(e.target.value)})}
                    >
                      <option value={0}>0 (zero-based)</option>
                      <option value={1}>1 (one-based)</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Batch Size (domains per API request)</Label>
                  <Input
                    type="number"
                    value={formData.batch_size || 100}
                    onChange={(e) => setFormData({...formData, batch_size: parseInt(e.target.value)})}
                    min={1}
                    max={1000}
                  />
                  <small className="text-muted">
                    Domains per API call
                  </small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Parallel Workers</Label>
                  <Input
                    type="number"
                    value={formData.parallel_workers || 1}
                    onChange={(e) => setFormData({...formData, parallel_workers: parseInt(e.target.value)})}
                    min={1}
                    max={10}
                  />
                  <small className="text-muted">
                    Parallel API calls (1-10)
                  </small>
                </FormGroup>
              </Col>
            </Row>

            {/* Response Mapping */}
            <h6 className="mt-4">Response Mapping</h6>
            <div className="mb-3">
              <Button color="info" size="sm" onClick={openMapperModal}>
                Configure Response Mapping
              </Button>
              {formData.response_mapping && (
                <Badge className="bg-dark text-success border border-success ms-2">
                  Mapped
                </Badge>
              )}
            </div>

            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                {' '}Active
              </Label>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditModal(false)}>Cancel</Button>
          <Button color="primary" onClick={handleSave}>
            {currentSource ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Test Modal */}
      <Modal isOpen={testModal} toggle={() => setTestModal(false)} size="xl">
        <ModalHeader toggle={() => setTestModal(false)}>
          Test Data Source: {currentSource?.name}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Domains (one per line)</Label>
            <Input
              type="textarea"
              value={testDomains}
              onChange={(e) => setTestDomains(e.target.value)}
              placeholder="subreg.cz&#10;regtons.com&#10;gransy.com"
              rows={5}
            />
          </FormGroup>

          <div className="mb-3">
            <Button color="primary" onClick={handleTest} disabled={testLoading || !testDomains}>
              {testLoading ? <Spinner size="sm" /> : <Play size={16} />}
              {' '}Test API
            </Button>
            <Button color="secondary" className="ms-2" onClick={() => {setTestDomains(''); setTestResults(null);}}>
              Clear
            </Button>
          </div>

          {/* CURL Example */}
          {currentSource && testDomains && (
            <div className="mb-3">
              <Label>CURL Example:</Label>
              <pre className="bg-dark text-white p-3 rounded" style={{fontSize: '11px'}}>
                {(() => {
                  const domains = testDomains.split('\n').map(d => d.trim()).filter(d => d);
                  let body = currentSource.request_body_template || '';
                  let url = currentSource.api_url;

                  // Replace {DOMAINS_INDEXED} in URL
                  if (currentSource.indexed_params_enabled && url.includes('{DOMAINS_INDEXED}')) {
                    const prefix = currentSource.indexed_params_prefix || 'item';
                    const countKey = currentSource.indexed_params_count_key || 'items';
                    const startIndex = currentSource.indexed_params_start_index || 0;

                    const indexedParams = [`${countKey}=${domains.length}`];
                    domains.forEach((domain, i) => {
                      indexedParams.push(`${prefix}${startIndex + i}=${domain}`);
                    });

                    url = url.replace('{DOMAINS_INDEXED}', indexedParams.join('&'));
                  }
                  // Replace {DOMAIN} in URL with first domain
                  else if (url.includes('{DOMAIN}') && domains.length > 0) {
                    url = url.replace('{DOMAIN}', domains[0]);
                  }

                  // Replace in body
                  if (body.includes('{DOMAINS_LIST}')) {
                    body = body.replace('{DOMAINS_LIST}', JSON.stringify(domains));
                  } else if (body.includes('{DOMAIN}') && domains.length > 0) {
                    body = body.replace('{DOMAIN}', domains[0]);
                  }

                  let curlCmd = `curl -X ${currentSource.request_method} '${url}'`;

                  // Add headers
                  curlCmd += ` \\\n  -H 'Content-Type: application/json'`;

                  // Add auth
                  if (currentSource.auth_type === 'basic' && currentSource.auth_credentials) {
                    curlCmd += ` \\\n  -u '${currentSource.auth_credentials.username}:${currentSource.auth_credentials.password}'`;
                  } else if (currentSource.auth_type === 'bearer' && currentSource.auth_credentials) {
                    curlCmd += ` \\\n  -H 'Authorization: Bearer ${currentSource.auth_credentials.token}'`;
                  } else if (currentSource.auth_type === 'api_key' && currentSource.auth_credentials) {
                    const key = currentSource.auth_credentials.key || 'X-API-Key';
                    curlCmd += ` \\\n  -H '${key}: ${currentSource.auth_credentials.value}'`;
                  }

                  // Add body
                  if (currentSource.request_method !== 'GET' && body) {
                    curlCmd += ` \\\n  -d '${body}'`;
                  }

                  return curlCmd;
                })()}
              </pre>
            </div>
          )}

          {/* Test Results */}
          {testResults && (
            <>
              <h6 className="mt-4">Results</h6>
              {testResults.success ? (
                <>
                  <Table dark size="sm" className="mb-3">
                    <thead>
                      <tr>
                        <th>Domain</th>
                        <th>{currentSource?.response_mapping?.main_value_label || 'Main Value'}</th>
                        {currentSource?.response_mapping?.additional_values?.map((val, idx) => (
                          <th key={idx}>{val.label}</th>
                        ))}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testResults.results?.map((result, idx) => (
                        <React.Fragment key={idx}>
                          <tr>
                            <td>{result.domain || '-'}</td>
                            <td>
                              <Badge className="bg-dark text-info border border-info">
                                {result.main_value || '-'}
                              </Badge>
                            </td>
                            {currentSource?.response_mapping?.additional_values?.map((val, vIdx) => (
                              <td key={vIdx}>
                                <small>{result.additional_data?.[val.label] || '-'}</small>
                              </td>
                            ))}
                            <td>
                              <Button
                                size="sm"
                                color="outline-info"
                                onClick={() => {
                                  const expanded = {...(testResults.expanded || {})};
                                  expanded[idx] = !expanded[idx];
                                  setTestResults({...testResults, expanded});
                                }}
                              >
                                <Eye size={14} /> View Raw
                              </Button>
                            </td>
                          </tr>
                          {testResults.expanded?.[idx] && (
                            <tr>
                              <td colSpan={3 + (currentSource?.response_mapping?.additional_values?.length || 0)} className="bg-secondary">
                                <pre className="bg-dark text-white p-3 rounded" style={{fontSize: '11px', maxHeight: '300px', overflow: 'auto'}}>
                                  {JSON.stringify(result.raw, null, 2)}
                                </pre>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>

                  <div className="mt-3">
                    <strong>Full Raw Response:</strong>
                    <pre className="bg-dark text-white p-3 rounded mt-2" style={{fontSize: '11px', maxHeight: '200px', overflow: 'auto'}}>
                      {testResults.raw_response}
                    </pre>
                  </div>
                </>
              ) : (
                <Alert color="danger">
                  Test failed: {testResults.error}
                </Alert>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setTestModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Response Mapper Modal */}
      <Modal isOpen={mapperModal} toggle={() => setMapperModal(false)} size="xl">
        <ModalHeader toggle={() => setMapperModal(false)}>
          Configure Response Mapping
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            Paste an example API response to automatically detect structure and configure field mapping.
          </p>

          <FormGroup>
            <Label>Example API Response</Label>
            <Input
              type="textarea"
              value={exampleResponse}
              onChange={(e) => setExampleResponse(e.target.value)}
              placeholder='Paste JSON/XML/CSV response here...'
              rows={8}
              className="font-monospace"
              style={{fontSize: '12px'}}
            />
          </FormGroup>

          <div className="mb-3">
            <Button
              color="primary"
              onClick={analyzeResponse}
              disabled={!exampleResponse}
            >
              Analyze Response
            </Button>
          </div>

          {/* Analysis Results */}
          {analyzedStructure && (
            <>
              <Alert color="info">
                Detected format: <strong>{analyzedStructure.format?.toUpperCase()}</strong>
                {' '} | Found {analyzedStructure.fields?.length || 0} fields
                {analyzedStructure.arrays?.length > 0 && ` | ${analyzedStructure.arrays.length} arrays`}
              </Alert>

              <h6>Configure Mapping</h6>

              {/* Array Path */}
              {analyzedStructure.arrays && analyzedStructure.arrays.length > 0 && (
                <FormGroup>
                  <Label>Array Path (where are the domain items?)</Label>
                  <Input
                    type="select"
                    value={mapping.array_path}
                    onChange={(e) => updateArrayPath(e.target.value)}
                  >
                    <option value="">Select array path...</option>
                    {analyzedStructure.arrays.map(path => (
                      <option key={path} value={path}>{path}</option>
                    ))}
                  </Input>
                  <small className="text-muted">
                    For multi-domain responses, select the path to the array of items
                  </small>
                </FormGroup>
              )}

              {/* Domain Field - use itemFields if array selected, otherwise all fields */}
              <FormGroup>
                <Label>Domain Field {analyzedStructure.itemFields && <small className="text-info">(from array item)</small>}</Label>
                <Input
                  type="select"
                  value={mapping.domain_field}
                  onChange={(e) => setMapping({...mapping, domain_field: e.target.value})}
                >
                  <option value="">Select field...</option>
                  {(analyzedStructure.itemFields || analyzedStructure.fields)?.map(field => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </Input>
                <small className="text-muted">
                  Which field contains the domain name? (e.g., "url", "domain", "name")
                </small>
              </FormGroup>

              {/* Main Value Field - use itemFields if array selected */}
              <FormGroup>
                <Label>Main Value Field {analyzedStructure.itemFields && <small className="text-info">(from array item)</small>}</Label>
                <Input
                  type="select"
                  value={mapping.main_value_field}
                  onChange={(e) => setMapping({...mapping, main_value_field: e.target.value})}
                >
                  <option value="">Select field...</option>
                  {(analyzedStructure.itemFields || analyzedStructure.fields)?.map(field => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </Input>
              </FormGroup>

              <FormGroup>
                <Label>Main Value Label (display name)</Label>
                <Input
                  type="text"
                  value={mapping.main_value_label}
                  onChange={(e) => setMapping({...mapping, main_value_label: e.target.value})}
                  placeholder="Backlinks"
                />
              </FormGroup>

              {/* Additional Values */}
              <h6 className="mt-4">Additional Values (optional)</h6>
              <p className="text-muted small">Add more fields to extract from each domain item</p>

              {(mapping.additional_values || []).map((val, idx) => (
                <Row key={idx} className="mb-2">
                  <Col md={5}>
                    <Input
                      type="select"
                      value={val.field}
                      onChange={(e) => {
                        const newVals = [...(mapping.additional_values || [])];
                        newVals[idx].field = e.target.value;
                        setMapping({...mapping, additional_values: newVals});
                      }}
                    >
                      <option value="">Select field...</option>
                      {(analyzedStructure.itemFields || analyzedStructure.fields)?.map(field => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={5}>
                    <Input
                      type="text"
                      value={val.label}
                      onChange={(e) => {
                        const newVals = [...(mapping.additional_values || [])];
                        newVals[idx].label = e.target.value;
                        setMapping({...mapping, additional_values: newVals});
                      }}
                      placeholder="Display label"
                    />
                  </Col>
                  <Col md={2}>
                    <Button
                      size="sm"
                      color="outline-danger"
                      onClick={() => {
                        const newVals = (mapping.additional_values || []).filter((_, i) => i !== idx);
                        setMapping({...mapping, additional_values: newVals});
                      }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </Col>
                </Row>
              ))}

              <Button
                size="sm"
                color="outline-info"
                onClick={() => {
                  const newVals = [...(mapping.additional_values || []), {field: '', label: ''}];
                  setMapping({...mapping, additional_values: newVals});
                }}
              >
                <Plus size={14} /> Add Field
              </Button>

              {/* Preview */}
              {mapping.domain_field && mapping.main_value_field && (
                <Alert color="success">
                  <strong>Mapping Preview:</strong>
                  <div className="mt-2">
                    <code>
                      {mapping.array_path && `${mapping.array_path} → `}
                      Domain: {mapping.domain_field} |
                      {mapping.main_value_label || 'Value'}: {mapping.main_value_field}
                    </code>
                  </div>
                </Alert>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setMapperModal(false)}>Cancel</Button>
          <Button
            color="primary"
            onClick={applyMapping}
            disabled={!mapping.domain_field || !mapping.main_value_field}
          >
            Apply Mapping
          </Button>
        </ModalFooter>
      </Modal>

      {/* Browse Data Modal */}
      <Modal isOpen={browseModal} toggle={() => setBrowseModal(false)} size="xl">
        <ModalHeader toggle={() => setBrowseModal(false)}>
          Browse Data: {currentSource?.name}
        </ModalHeader>
        <ModalBody>
          {browseLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : browseData.length === 0 ? (
            <Alert color="info">No data available for this source yet. Run a test or wait for scheduled enrichment.</Alert>
          ) : (
            <>
              <Table dark size="sm" striped>
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>{currentSource?.response_mapping?.main_value_label || 'Main Value'}</th>
                    {currentSource?.response_mapping?.additional_values?.map((val, idx) => (
                      <th key={idx}>{val.label}</th>
                    ))}
                    <th>Fetched At</th>
                  </tr>
                </thead>
                <tbody>
                  {browseData.map((row, idx) => (
                    <tr key={idx}>
                      <td><small>{row.domain}</small></td>
                      <td>
                        <Badge className="bg-dark text-info border border-info">
                          {row.main_value_numeric || '-'}
                        </Badge>
                      </td>
                      {currentSource?.response_mapping?.additional_values?.map((val, vIdx) => (
                        <td key={vIdx}>
                          <small>{row[val.field.toLowerCase().replace(/[^a-z0-9]/g, '_')] || '-'}</small>
                        </td>
                      ))}
                      <td><small className="text-muted">{new Date(row.fetched_at).toLocaleString()}</small></td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  Showing {browseData.length} of {browseTotal} records
                </small>
                <div className="btn-group">
                  <Button
                    size="sm"
                    disabled={browsePage <= 1}
                    onClick={() => {
                      const newPage = browsePage - 1;
                      setBrowsePage(newPage);
                      loadBrowseData(currentSource.id, newPage);
                    }}
                  >
                    Previous
                  </Button>
                  <Button size="sm" disabled>
                    Page {browsePage}
                  </Button>
                  <Button
                    size="sm"
                    disabled={browseData.length < 50}
                    onClick={() => {
                      const newPage = browsePage + 1;
                      setBrowsePage(newPage);
                      loadBrowseData(currentSource.id, newPage);
                    }}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setBrowseModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>

    </Container>
  );
};

export default DataSources;
